/**
 * Route Components Registry
 *
 * This module provides platform-aware component loading for routes.
 * On iOS native apps, components are eagerly loaded during splash screen
 * to eliminate loading delays when navigating.
 * On web, components are lazy-loaded for better initial page load.
 */

import { Capacitor } from '@capacitor/core'

// Check if we're running on a native platform
const isNative = Capacitor.isNativePlatform()

// Component import functions - these are the dynamic imports
const componentImports = {
  // Layouts
  LayoutV2: () => import('@/views/layouts/LayoutV2'),
  LayoutPublic: () => import('@/views/layouts/LayoutPublic'),
  LayoutWeb: () => import('@/views/layouts/LayoutWeb'),

  // Public views
  ViewPublicIndex: () => import('@/views/v2/ViewPublicIndex'),
  ViewPublicWhatsNew: () => import('@/views/v2/ViewPublicWhatsNew'),
  ViewPublic: () => import('@/views/ViewPublic'),

  // Main app views
  ViewWebIndex: () => import('@/views/v2/ViewWebIndex'),
  ViewSearch: () => import('@/views/v2/ViewSearch'),
  ViewWebTrending: () => import('@/views/v2/ViewWebTrending'),
  ViewCircle: () => import('@/views/v2/ViewCircle'),
  IonRefresherTest: () => import('@/views/v2/IonRefresherTest'),
  ViewTitle: () => import('@/views/ViewTitle'),
  ViewWebAllLists: () => import('@/views/v2/ViewWebAllLists'),
  ViewWebUserLists: () => import('@/views/v2/ViewWebUserLists'),
  ViewWebFeed: () => import('@/views/v2/ViewWebFeed'),
  ViewWebActivity: () => import('@/views/v2/ViewWebActivity'),
  ViewWebClubs: () => import('@/views/v2/ViewWebClubs'),
  ViewWebClubCreate: () => import('@/views/v2/ViewWebClubCreate'),
  ViewWebClubDetail: () => import('@/views/v2/ViewWebClubDetail'),
  ViewWebClubSettings: () => import('@/views/v2/ViewWebClubSettings'),
  ViewWebClubAdmin: () => import('@/views/v2/ViewWebClubAdmin'),
  ViewWebClubJoin: () => import('@/views/v2/ViewWebClubJoin'),
  ViewWebListJoin: () => import('@/views/v2/ViewWebListJoin'),
  ViewWebListUserInvite: () => import('@/views/v2/ViewWebListUserInvite'),
  ViewWebNotifications: () => import('@/views/v2/ViewWebNotifications'),
  ViewWebUserListSystem: () => import('@/views/v2/ViewWebUserListSystem'),
  ViewWebProfile: () => import('@/views/v2/ViewWebProfile'),
  ViewWebProfileEdit: () => import('@/views/v2/ViewWebProfileEdit'),
  ViewSettings: () => import('@/views/v2/ViewSettings'),
  ViewSettingsAdvanced: () => import('@/views/v2/ViewSettingsAdvanced'),
  ViewSettingsImport: () => import('@/views/v2/ViewSettingsImport'),
  ViewSettingsNotifications: () => import('@/views/v2/ViewSettingsNotifications'),
  ViewWebPreferences: () => import('@/views/v2/ViewWebPreferences'),
  ViewHomeCustomization: () => import('@/views/v2/ViewHomeCustomization'),
  ViewWebPeople: () => import('@/views/v2/ViewWebPeople'),
  ViewOnboarding: () => import('@/views/v2/ViewOnboarding'),
  ViewWebAdmin: () => import('@/views/v2/ViewWebAdmin'),
  ViewWebAdminBounce: () => import('@/views/v2/ViewWebAdminBounce'),
  ViewWebUser: () => import('@/views/v2/ViewWebUser'),
  ViewWebReviewsDetail: () => import('@/views/v2/ViewWebReviewsDetail'),
  ViewWebUserListSingle: () => import('@/views/v2/ViewWebUserListSingle'),
  ViewTitlePerson: () => import('@/views/ViewTitlePerson'),
  ViewTitleCast: () => import('@/views/ViewTitleCast'),

  // Auth and utility views (less commonly accessed, lower priority)
  ViewAuthMagicLink: () => import('@/views/ViewAuthMagicLink'),
  ViewAuth: () => import('@/views/ViewAuth'),
  ViewRegister: () => import('@/views/ViewRegister'),
  ViewPrivacyPolicy: () => import('@/views/ViewPrivacyPolicy'),
  ViewTermsOfService: () => import('@/views/ViewTermsOfService'),
  ViewSupport: () => import('@/views/ViewSupport'),
  ViewNotFound: () => import('@/views/ViewNotFound.vue')
}

// Cache for preloaded components (used on native)
const preloadedComponents = {}

// Track preload status
let preloadStarted = false
let preloadComplete = false
const preloadPromise = { resolve: null, promise: null }

/**
 * Get a component for use in routes.
 * On native: returns a function that resolves to the preloaded component
 * On web: returns the dynamic import function for lazy loading
 */
export function getRouteComponent(name) {
  if (!componentImports[name]) {
    console.warn(`[RouteComponents] Unknown component: ${name}`)
    return () => import('@/views/v2/ViewWebIndex') // Fallback
  }

  if (isNative) {
    // On native, return a function that resolves to the preloaded component
    // or loads it if not yet preloaded
    return () => {
      if (preloadedComponents[name]) {
        return Promise.resolve(preloadedComponents[name])
      }
      // Fallback to dynamic import if not preloaded yet
      return componentImports[name]()
    }
  }

  // On web, use lazy loading
  return componentImports[name]
}

/**
 * Preload all route components.
 * Should be called during app initialization on native platforms.
 * Returns a promise that resolves when all components are loaded.
 */
export async function preloadAllComponents() {
  if (!isNative) {
    console.log('[RouteComponents] Skipping preload on web')
    return Promise.resolve()
  }

  if (preloadStarted) {
    // Return existing promise if preload already started
    return preloadPromise.promise
  }

  preloadStarted = true
  preloadPromise.promise = new Promise((resolve) => {
    preloadPromise.resolve = resolve
  })

  console.log('[RouteComponents] Starting component preload for native app...')
  const startTime = Date.now()

  // Priority components - load these first (most commonly accessed views)
  const priorityComponents = [
    'LayoutWeb',
    'ViewWebIndex',
    'ViewWebProfile',
    'ViewWebFeed',
    'ViewTitle',
    'ViewSearch',
    'ViewWebActivity'
  ]

  // Load priority components first
  const priorityPromises = priorityComponents.map(async (name) => {
    try {
      const module = await componentImports[name]()
      preloadedComponents[name] = module
      return { name, success: true }
    } catch (error) {
      console.error(`[RouteComponents] Failed to preload ${name}:`, error)
      return { name, success: false }
    }
  })

  await Promise.all(priorityPromises)
  console.log(
    `[RouteComponents] Priority components loaded in ${Date.now() - startTime}ms`
  )

  // Load remaining components in background
  const remainingComponents = Object.keys(componentImports).filter(
    (name) => !priorityComponents.includes(name)
  )

  const remainingPromises = remainingComponents.map(async (name) => {
    try {
      const module = await componentImports[name]()
      preloadedComponents[name] = module
      return { name, success: true }
    } catch (error) {
      console.error(`[RouteComponents] Failed to preload ${name}:`, error)
      return { name, success: false }
    }
  })

  await Promise.all(remainingPromises)

  preloadComplete = true
  const totalTime = Date.now() - startTime

  console.log(
    `[RouteComponents] All ${Object.keys(componentImports).length} components preloaded in ${totalTime}ms`
  )

  preloadPromise.resolve()
  return preloadPromise.promise
}

/**
 * Check if preloading is complete
 */
export function isPreloadComplete() {
  return preloadComplete
}

/**
 * Wait for preload to complete
 */
export function waitForPreload() {
  if (!isNative || preloadComplete) {
    return Promise.resolve()
  }
  return preloadPromise.promise || Promise.resolve()
}
